import { useParams, Link, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Clock,
  ChefHat,
  Users,
  Flame,
  List,
  Utensils,
} from 'lucide-react'
import { SEO } from '@/components/SEO'
import { recipesData, getRecipeById } from './recipesData'
import type { SupportedLanguage } from '@/i18n'

export function RecipeDetailPage() {
  const { t, i18n } = useTranslation('common')
  const { recipeId } = useParams<{ recipeId: string }>()
  const lang = i18n.language as SupportedLanguage

  const recipe = getRecipeById(recipeId || '')

  if (!recipe) {
    const recipesListPath = lang === 'it' ? `/${lang}/ricette` : `/${lang}/recipes`
    return <Navigate to={recipesListPath} replace />
  }

  const recipesListPath = lang === 'it' ? `/${lang}/ricette` : `/${lang}/recipes`
  const tk = recipe.translationKey

  const title = t(`recipes.recipes.${tk}.title`)
  const description = t(`recipes.recipes.${tk}.description`)
  const difficulty = t(`recipes.recipes.${tk}.difficulty`) as string

  const ingredients = (t(`recipes.recipes.${tk}.ingredients`, {
    returnObjects: true,
  }) as string[]) || []
  const steps = (t(`recipes.recipes.${tk}.steps`, { returnObjects: true }) as string[]) || []

  const getDifficultyColor = (d: string) => {
    switch (d) {
      case 'easy':
        return 'bg-green-100 text-green-700 border-green-200'
      case 'medium':
        return 'bg-amber-100 text-amber-700 border-amber-200'
      case 'hard':
        return 'bg-red-100 text-red-700 border-red-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const getDifficultyIcon = (d: string) => {
    switch (d) {
      case 'easy':
        return <ChefHat className="w-4 h-4" />
      case 'medium':
      case 'hard':
        return <Flame className="w-4 h-4" />
      default:
        return <ChefHat className="w-4 h-4" />
    }
  }

  const getDifficultyLabel = (d: string) => {
    switch (d) {
      case 'easy':
        return t('recipes.featured.easy')
      case 'medium':
        return t('recipes.featured.medium')
      case 'hard':
        return t('recipes.featured.hard')
      default:
        return d
    }
  }

  const relatedRecipes = recipesData
    .filter((r) => r.id !== recipe.id && r.category === recipe.category)
    .slice(0, 3)

  return (
    <>
      <SEO
        title={`${title} | ${t('nav.recipes')}`}
        description={description}
        type="article"
        image={recipe.image}
      />

      <main id="main-content">
        {/* Hero Image */}
        <section className="relative h-[50vh] min-h-[320px] max-h-[600px] overflow-hidden">
          <img
            src={recipe.image}
            alt={title}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.src = '/images/prodotti_in_evidenza.png'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/80 via-[#4E2118]/30 to-transparent" />

          {/* Back Button */}
          <div className="absolute top-6 left-6 z-10">
            <Link
              to={recipesListPath}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur-sm text-[#4E2118] text-sm font-semibold rounded-full shadow-lg hover:bg-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('recipes.detail.backToRecipes')}
            </Link>
          </div>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0">
            <div className="container-fiorucci pb-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#DF631E] text-white text-xs font-bold rounded-full shadow-md">
                    <Utensils className="w-3.5 h-3.5" />
                    {recipe.category}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full border ${getDifficultyColor(
                      difficulty
                    )}`}
                  >
                    {getDifficultyIcon(difficulty)}
                    {getDifficultyLabel(difficulty)}
                  </span>
                </div>

                <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-4xl">
                  {title}
                </h1>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="section-padding bg-[#FDF8F3]">
          <div className="container-fiorucci">
            <div className="max-w-4xl mx-auto">
              {/* Meta Bar */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="flex flex-wrap gap-4 mb-10"
              >
                <div className="flex items-center gap-2 px-5 py-3 bg-white rounded-full shadow-sm">
                  <Clock className="w-5 h-5 text-[#DF631E]" />
                  <span className="text-sm font-medium text-[#4E2118]">
                    {t('recipes.detail.prepTime')}: {t(`recipes.recipes.${tk}.time`)}
                  </span>
                </div>
                <div className="flex items-center gap-2 px-5 py-3 bg-white rounded-full shadow-sm">
                  <Users className="w-5 h-5 text-[#5C1A1A]" />
                  <span className="text-sm font-medium text-[#4E2118]">
                    {t('recipes.detail.servings')}: {recipe.servings}
                  </span>
                </div>
              </motion.div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="text-body-lg text-[#4E2118]/80 leading-relaxed mb-12"
              >
                {description}
              </motion.p>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Ingredients */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-white rounded-3xl p-6 md:p-8 shadow-xl h-fit"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#DF631E]/10 flex items-center justify-center">
                      <List className="w-5 h-5 text-[#DF631E]" />
                    </div>
                    <h2 className="font-serif text-xl font-bold text-[#4E2118]">
                      {t('recipes.detail.ingredients')}
                    </h2>
                  </div>
                  <ul className="space-y-3">
                    {ingredients.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-[#4E2118]/80">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#DF631E] shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Steps */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="lg:col-span-2 bg-white rounded-3xl p-6 md:p-8 shadow-xl"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#5C1A1A]/10 flex items-center justify-center">
                      <Utensils className="w-5 h-5 text-[#5C1A1A]" />
                    </div>
                    <h2 className="font-serif text-xl font-bold text-[#4E2118]">
                      {t('recipes.detail.steps')}
                    </h2>
                  </div>
                  <div className="space-y-6">
                    {steps.map((step, idx) => (
                      <div key={idx} className="flex gap-4">
                        <div className="w-8 h-8 rounded-full bg-[#FDF8F3] text-[#DF631E] font-bold flex items-center justify-center shrink-0">
                          {idx + 1}
                        </div>
                        <p className="text-[#4E2118]/80 leading-relaxed pt-0.5">{step}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Back Link */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-12 pt-8 border-t border-[#F3E4D6]"
              >
                <Link
                  to={recipesListPath}
                  className="inline-flex items-center gap-2 text-[#DF631E] font-semibold hover:gap-3 transition-all"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('recipes.detail.backToRecipes')}
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Related Recipes */}
        {relatedRecipes.length > 0 && (
          <section className="section-padding bg-white">
            <div className="container-fiorucci">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-10"
              >
                <h2 className="heading-section text-[#4E2118] text-center">
                  {t('recipes.detail.relatedRecipes')}
                </h2>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedRecipes.map((item, index) => (
                  <motion.article
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group bg-[#FDF8F3] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Link to={`${recipesListPath}/${item.id}`} className="block">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={item.image}
                          alt={t(`recipes.recipes.${item.translationKey}.title`)}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.src = '/images/prodotti_in_evidenza.png'
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/60 via-transparent to-transparent" />
                        <div className="absolute top-4 left-4 px-3 py-1 bg-white/95 backdrop-blur-sm text-[#4E2118] text-xs font-bold rounded-full shadow-sm">
                          {item.category}
                        </div>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 text-[#4E2118]/60 text-sm mb-2">
                          <Clock className="w-4 h-4 text-[#DF631E]" />
                          <span>{item.time}</span>
                        </div>
                        <h3 className="font-serif text-lg font-bold text-[#4E2118] group-hover:text-[#DF631E] transition-colors line-clamp-2">
                          {t(`recipes.recipes.${item.translationKey}.title`)}
                        </h3>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  )
}
